import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

interface RequiredTooltipContextValue {
  /** true durante a "introdução" (mostra todos os tooltips ao entrar). */
  introActive: boolean;
}

const RequiredTooltipContext =
  createContext<RequiredTooltipContextValue | null>(null);

interface RequiredTooltipProviderProps {
  children: ReactNode;
  /** Duração da introdução em ms (todos os tooltips visíveis). Padrão 2s. */
  introMs?: number;
  /**
   * Chave que, ao mudar, reinicia a introdução (ex.: passo atual do formulário).
   * Assim, ao trocar de etapa, os tooltips daquela etapa piscam de novo.
   */
  restartKey?: string | number;
  /** Quando false, desliga os tooltips (contexto nulo) — ex.: produto não residencial. */
  enabled?: boolean;
}

/**
 * Coordena os tooltips de campo obrigatório: ao montar (ou quando `restartKey`
 * muda) mantém todos visíveis por `introMs` e depois os deixa apenas no hover.
 * Campos fora deste provider não exibem tooltip (passthrough) — por isso o
 * recurso só aparece nas telas que envolvem o provider.
 */
export function RequiredTooltipProvider({
  children,
  introMs = 2000,
  restartKey,
  enabled = true,
}: RequiredTooltipProviderProps) {
  const [introActive, setIntroActive] = useState(true);

  useEffect(() => {
    setIntroActive(true);
    const timer = setTimeout(() => setIntroActive(false), introMs);
    return () => clearTimeout(timer);
  }, [introMs, restartKey]);

  return (
    <RequiredTooltipContext.Provider value={enabled ? { introActive } : null}>
      {children}
    </RequiredTooltipContext.Provider>
  );
}

/** Retorna o contexto (ou null se o campo não estiver sob o provider). */
export function useRequiredTooltip(): RequiredTooltipContextValue | null {
  return useContext(RequiredTooltipContext);
}
